/**
 * Helpers for the public Document Capture API (see docs/document-capture-flow.md).
 *
 * The capture session runs on the Frontoffice domain; the reservation flow only
 * embeds the capture URL (QR-code on desktop, iframe on mobile), polls the session
 * status and maps the delivered result onto the registration form.
 */

export const DOCUMENT_CAPTURE_MESSAGE_SOURCE = "mobielrijden.document_capture";
export const DOCUMENT_CAPTURE_MESSAGE_VERSION = 1;
export const DOCUMENT_CAPTURE_POLL_INTERVAL_MS = 2500;
export const DOCUMENT_CAPTURE_SESSION_TTL_MINUTES = 60;
export const DOCUMENT_CAPTURE_QR_SIZE = 340;
export const DOCUMENT_CAPTURE_REQUESTED_BY = "newway-website";

const CLOSED_SESSION_STATUSES = new Set(["cancelled", "expired"]);
const READY_SESSION_STATUSES = new Set(["completed", "delivered"]);

// Canonical OCR v2 fields the registration form consumes (`recognition[].ocr`).
const REGISTRATION_OCR_FIELDS = [
    "first_name",
    "last_name",
    "full_name",
    "country",
    "nationality",
    "number",
    "expiration_date",
    "date_of_birth",
    "drivers_license_categories",
];

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeString = (value) => String(value ?? "").trim();

const isUsableValue = (value) => {
    if (Array.isArray(value)) return value.length > 0;
    const normalized = normalizeString(value);
    return Boolean(normalized) && normalized.toLowerCase() !== "n/a" && normalized.toLowerCase() !== "null";
};

// Recognition values are "usually" ISO formatted, but MRZ-derived output can be
// alpha-3 countries, YYMMDD dates or spaced document numbers. Everything is brought
// to the OCR v2 shape here so the form and the auto-continue check see one format.
const COUNTRY_ALPHA3_TO_ALPHA2 = {
    NLD: "NL", BEL: "BE", DEU: "DE", FRA: "FR", GBR: "GB", LUX: "LU", ESP: "ES",
    ITA: "IT", PRT: "PT", AUT: "AT", CHE: "CH", POL: "PL", DNK: "DK", SWE: "SE",
    NOR: "NO", FIN: "FI", IRL: "IE", CZE: "CZ", SVK: "SK", HUN: "HU", ROU: "RO",
    BGR: "BG", GRC: "GR", HRV: "HR", SVN: "SI", LTU: "LT", LVA: "LV", EST: "EE",
    TUR: "TR", UKR: "UA", USA: "US", CAN: "CA", MAR: "MA", SUR: "SR", IDN: "ID",
};

const normalizeCountryCode = (value) => {
    const normalized = normalizeString(value).toUpperCase();
    if (normalized.length === 3 && COUNTRY_ALPHA3_TO_ALPHA2[normalized]) {
        return COUNTRY_ALPHA3_TO_ALPHA2[normalized];
    }
    return normalized;
};

const normalizeDocumentNumber = (value) => normalizeString(value).replace(/\s+/g, "").toUpperCase();

// `isBirthDate` picks the century for two-digit years: a birth year above the
// current one is in the past century, an expiry date is always in the future.
const normalizeIsoDate = (value, {isBirthDate = false} = {}) => {
    const normalized = normalizeString(value);
    if (!normalized) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

    const dmy = normalized.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dmy) {
        const [, day, month, year] = dmy;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    const ymdCompact = normalized.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (ymdCompact) {
        const [, year, month, day] = ymdCompact;
        return `${year}-${month}-${day}`;
    }

    const mrz = normalized.match(/^(\d{2})(\d{2})(\d{2})$/);
    if (mrz) {
        const [, yy, month, day] = mrz;
        const currentYy = new Date().getFullYear() % 100;
        const century = isBirthDate && Number(yy) > currentYy ? 1900 : 2000;
        return `${century + Number(yy)}-${month}-${day}`;
    }

    return normalized;
};

const OCR_FIELD_NORMALIZERS = {
    country: normalizeCountryCode,
    nationality: normalizeCountryCode,
    number: normalizeDocumentNumber,
    expiration_date: (value) => normalizeIsoDate(value),
    date_of_birth: (value) => normalizeIsoDate(value, {isBirthDate: true}),
};

export const isDocumentCaptureSessionClosed = (session) => (
    CLOSED_SESSION_STATUSES.has(normalizeString(session?.status).toLowerCase())
);

/**
 * `complete` only means every photo is present; the result may be requested
 * once the recognition finished as well (status = completed + recognized_at).
 */
export const isDocumentCaptureSessionReady = (session) => (
    READY_SESSION_STATUSES.has(normalizeString(session?.status).toLowerCase()) &&
    Boolean(session?.recognized_at)
);

export const parseDocumentCaptureMessage = (event, {expectedOrigin, expectedSource}) => {
    if (!event) return "";
    if (expectedOrigin && event.origin !== expectedOrigin) return "";
    if (expectedSource && event.source !== expectedSource) return "";

    const data = event.data;
    if (!isPlainObject(data)) return "";
    if (data.source !== DOCUMENT_CAPTURE_MESSAGE_SOURCE) return "";
    if (data.version !== DOCUMENT_CAPTURE_MESSAGE_VERSION) return "";

    const eventName = normalizeString(data.event).toLowerCase();
    return eventName === "ready" || eventName === "completed" ? eventName : "";
};

export const getDocumentCaptureOrigin = (captureUrl) => {
    try {
        return new URL(String(captureUrl || "")).origin;
    } catch (_error) {
        return "";
    }
};

/**
 * Images are matched by `document_type` + `side` (never by array index). Selfie
 * images carry `side: null`.
 */
export const findDocumentCaptureImage = (images, {documentType, side = null}) => {
    if (!Array.isArray(images)) return null;
    const wantedType = normalizeString(documentType).toLowerCase();
    const wantedSide = side === null ? null : normalizeString(side).toLowerCase();

    return images.find((image) => {
        if (normalizeString(image?.document_type).toLowerCase() !== wantedType) return false;
        if (wantedSide === null) return true;
        return normalizeString(image?.side).toLowerCase() === wantedSide;
    }) || null;
};

/**
 * `content` is raw base64 without the data URL prefix; the browser assembles the
 * data URL from `mime_type` + `content`.
 */
export const toDocumentCaptureImageDataUrl = (image) => {
    const content = normalizeString(image?.content);
    if (!content) return "";
    const mimeType = normalizeString(image?.mime_type) || "image/jpeg";
    return `data:${mimeType};base64,${content}`;
};

export const findDocumentCaptureRecognition = (recognition, documentType) => {
    if (!Array.isArray(recognition)) return null;
    const wantedType = normalizeString(documentType).toLowerCase();
    return recognition.find((entry) => (
        normalizeString(entry?.document_type).toLowerCase() === wantedType
    )) || null;
};

/**
 * Picks the registration fields from `recognition[].ocr`. MRZ is not decomposed by
 * the API (only raw `full_mrz` / `nfc_mrz`), so it is not used for prefilling.
 * Returns the same shape as the OCR v2 endpoint so the form logic is shared.
 */
export const extractDocumentCaptureRecognitionFields = (recognition, documentType = "drivers_license") => {
    const entry = findDocumentCaptureRecognition(recognition, documentType);
    const ocr = isPlainObject(entry?.ocr) ? entry.ocr : {};
    const result = {};

    REGISTRATION_OCR_FIELDS.forEach((fieldName) => {
        if (!isUsableValue(ocr[fieldName])) return;
        const normalize = OCR_FIELD_NORMALIZERS[fieldName];
        result[fieldName] = normalize ? normalize(ocr[fieldName]) : ocr[fieldName];
    });

    return result;
};
